import React, { Fragment, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { Breadcrumbs, H4 } from '../../../../AbstractElements';
import loadingScreenQuizApi from '../../../../api/loadingscreenQuiz';
import LoadingScreenCard from './LoadingScreenCard';
import LoadingScreenModal from './LoadingScreenModal';
import LoadingScreenFilters from './LoadingScreenFilters';

const LoadingScreenManagementContain = () => {
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal
  const [modal, setModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    file: null,
    filename: '',
    mimetype: '',
    size: 0,
    time: 5000,
    isActive: true
  });

  // Load screens
  useEffect(() => {
    loadScreens();
  }, [searchTerm, statusFilter]);

  const loadScreens = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      
      const response = await loadingScreenQuizApi.getAllLoadingScreens(params);
      setScreens(response.data?.loadingScreens || response.loadingScreens || response.data || response);
    } catch (err) {
      console.error('❌ Error loading screens:', err);
      setError('Không thể tải danh sách loading screens');
      setScreens([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setEditingScreen(null);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      file: null,
      filename: '',
      mimetype: '',
      size: 0,
      time: 5000,
      isActive: true
    });
  };

  const handleEdit = (screen) => {
    setEditingScreen(screen);
    setFormData({
      file: null, // Don't load file for editing
      filename: screen.filename,
      mimetype: screen.mimetype || '',
      size: screen.size || 0,
      time: screen.time || 5000,
      isActive: screen.isActive
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      if (editingScreen) {
        // Update existing screen (no file upload)
        const updateData = {
          filename: formData.filename,
          time: formData.time,
          isActive: formData.isActive
        };
        await loadingScreenQuizApi.updateLoadingScreen(editingScreen.id, updateData);
        setSuccess('Cập nhật loading screen thành công!');
      } else {
        // Create new screen (with file upload)
        if (!formData.file) {
          setError('Vui lòng chọn file để upload');
          return;
        }
        await loadingScreenQuizApi.createLoadingScreen(formData);
        setSuccess('Tạo loading screen thành công!');
      }
      
      toggleModal();
      loadScreens();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error saving loading screen:', err);
      setError('Không thể lưu loading screen');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa loading screen này?')) return;
    
    try {
      await loadingScreenQuizApi.deleteLoadingScreen(id);
      setSuccess('Xóa loading screen thành công!');
      loadScreens();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error deleting loading screen:', err);
      setError('Không thể xóa loading screen');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await loadingScreenQuizApi.toggleLoadingScreenActive(id);
      setSuccess('Cập nhật trạng thái thành công!');
      loadScreens();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      setError('Không thể cập nhật trạng thái');
    }
  };

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Quiz' title='Loading Screens' mainTitle='Loading Screens' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải loading screens...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent='Quiz' title='Loading Screens' mainTitle='Quản lý Loading Screens' />
      <Container fluid={true}>
        {/* Success/Error Messages */}
        {success && <Alert color="success">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}

        {/* Header Card */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col md="6">
                <H4 className="text-dark mb-0">
                  <i className="fa fa-image me-2 text-primary"></i>
                  Quản lý Loading Screens
                </H4>
              </Col>
              <Col md="6" className="text-end">
                <Button color="primary" onClick={toggleModal}>
                  <i className="fa fa-upload me-2"></i>
                  Upload Loading Screen
                </Button>
              </Col>
            </Row>

            {/* Filters */}
            <LoadingScreenFilters 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </CardBody>
        </Card>

        {/* Loading Screens List */}
        <Row>
          {screens.length === 0 ? (
            <Col>
              <Card className="text-center py-5">
                <CardBody>
                  <i className="fa fa-image text-muted" style={{fontSize: '3rem'}}></i>
                  <p className="text-muted mt-3">Chưa có loading screen nào</p>
                  <Button color="primary" onClick={toggleModal}>
                    <i className="fa fa-upload me-2"></i>
                    Upload Loading Screen
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ) : (
            screens.map((screen) => (
              <Col lg="4" md="6" key={screen.id}>
                <LoadingScreenCard 
                  screen={screen}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              </Col>
            ))
          )}
        </Row>

        {/* Create/Edit Modal */}
        <LoadingScreenModal 
          isOpen={modal}
          toggle={toggleModal}
          editingScreen={editingScreen}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
        />
      </Container>
    </Fragment>
  );
};

export default LoadingScreenManagementContain;

